<template>
  <main class="statements-wrapper">
    <h1>Statements</h1>
    <p>
      Receive your statements and your transaction history using the form below.
      The generated documents will be sent over to you
      <strong>by email</strong> in <em>.pdf</em> and <em>.csv</em>.
    </p>
    <form @submit.prevent="requestStatements()">
      <fieldset>
        <legend>Request parameter</legend>
        <TransitionGroup name="fade">
          <label v-if="!isMonthly && isDailyStatementsAllowed" key="date">
            Select the date (DD-MM-YYYY)
            <input
              type="date"
              :min="minDateStatements"
              :max="getLocalDate(YESTERDAY)"
              v-model="statementForm.chosenDate"
              :class="{ invalid: errors.hasDatesError || errors.invalidDates }" />
          </label>
          <label v-else key="month">
            Select the month (YYYY-MM)
            <input
              type="month"
              :min="minMonthStatements"
              :max="maxMonthStatements"
              v-model="statementForm.chosenMonth"
              :class="{ invalid: errors.hasDatesError || errors.invalidDates }" />
          </label>
        </TransitionGroup>
        <label class="checkbox" v-if="isDailyStatementsAllowed">
          <input type="checkbox" v-model="isMonthly" />
          Whole month
        </label>
      </fieldset>
      <div class="action-group">
        <button type="submit" class="btn icon-btn save">
          Generate Documents
        </button>
        <button
          type="reset"
          class="btn icon-btn reset"
          @click.prevent="handleReset()">
          Reset date
        </button>
      </div>
      <ol
        class="errors"
        v-if="errors.hasNoDatesError ||
          errors.invalidDates ||
          errors.datesEarlierThanLimit
          ">
        <li v-if="errors.hasNoDatesError">Please select a month.</li>
        <li v-if="errors.invalidDates">
          Selected Month can only be between Dec 2022 and current Month-1.
        </li>
        <li v-if="errors.datesEarlierThanLimit">
          The earliest date possible is the 1st December 2022. Please request
          later dates.
        </li>
      </ol>
    </form>
    <Transition name="slide-fade">
      <p class="result success" v-if="isRequestSuccessful">
        Your documents are being prepared. An email with all the generated
        documents will be sent to your email address once it's ready.
      </p>
    </Transition>
    <Transition name="slide-fade">
      <p class="result spam" v-if="isRequestSpam">
        There is currently a pending request. Please try again in 2 minutes.
      </p>
    </Transition>
    <Transition name="slide-fade">
      <p class="result deny" v-if="isRequestError">
        The request wasn't successful. Please try again or contact our support
        team.
      </p>
    </Transition>
    <article
      class="latest-request"
      v-if="latestRequest && latestRequest.status">
      Here is the last request you made and its status :
      <dl>
        <div>
          <dt>Status</dt>
          <dd>
            <Tag :class="latestRequest.status">{{ latestRequest.status }}</Tag>
          </dd>
        </div>
        <div>
          <dt>Requested on</dt>
          <dd>{{ formatDateWithTime(latestRequest.requestedOn) }}</dd>
        </div>
        <div>
          <dt>Sending to</dt>
          <dd>{{ latestRequest.email }}</dd>
        </div>
        <div>
          <dt>Requested From</dt>
          <dd>{{ formatDate(latestRequest.from) }}</dd>
        </div>
        <div>
          <dt>Requested To</dt>
          <dd>{{ formatDate(latestRequest.to) }}</dd>
        </div>
      </dl>
    </article>
    <article class="gst-wrapper">
      <h2>GST invoicing</h2>
      <p>
        Choose up to 12 months and generate the GST invoice. The generated
        documents will be available for download or sent via email in PDF
        format.
      </p>
      <form @submit.prevent="requestGstInvoicesPDF()">
        <fieldset>
          <legend>Months range (included)</legend>
          <label>
            From month (YYYY-MM)
            <input
              type="month"
              :min="minMonthGst"
              :max="gstForm.chosenToMonth || maxMonthGst"
              v-model="gstForm.chosenFromMonth" /><!-- :class="{ invalid: errors.hasDatesError || errors.invalidDates }" -->
          </label>
          <label>
            To month (YYYY-MM)
            <input
              type="month"
              :min="gstChosenFromMonth"
              :max="maxMonthGst"
              v-model="gstForm.chosenToMonth"
              :disabled="!gstChosenFromMonth" /><!-- :class="{ invalid: errors.hasDatesError || errors.invalidDates }" -->
          </label>
          <button
            class="btn icon-btn save"
            @click.prevent="requestGstInvoicesPDF()"
            :disabled="!gstChosenFromMonth">
            Download PDF
          </button>
        </fieldset>
        <p v-if="gstPdfErrorMsg" class="error">{{ gstPdfErrorMsg }}</p>
        <fieldset class="gst-recipients">
          <h3>Send to recipients</h3>
          <ul v-if="gstRecipients.length > 0">
            <li v-for="(recipient, index) of gstRecipients" :key="recipient">
              {{ recipient
              }}<button
                class="btn remove"
                @click.prevent="removeGstRecipient(index)">
                X
              </button>
            </li>
          </ul>
          <label>
            New recipient email
            <input type="email" placeholder="Email" v-model="newGstRecipient" />
          </label>
          <button
            class="btn icon-btn plus outline"
            @click.prevent="addGstRecipient()">
            Add as GST invoice recipient
          </button>
          <button
            class="btn icon-btn email"
            @click.prevent="sendGstInvoiceByEmail()"
            :disabled="!gstRecipients.length > 0">
            Email to recipients
          </button>
          <p v-if="gstEmailErrorMessage" class="error">
            {{ gstEmailErrorMessage }}
          </p>
          <p v-else-if="isGstEmailSuccessful" class="success">
            An email has been sent to the selected recipient.
          </p>
        </fieldset>
      </form>
    </article>
  </main>
</template>
<script>
import Tag from '@/atoms/Tag.vue';
const MIN_MONTH_STATEMENTS = '2022-12';
const MIN_DATE_STATEMENTS = '2022-12-01';
const MIN_MONTH_GST = '2023-01';
export default {
  data() {
    return {
      TODAY: new Date(),
      YESTERDAY: new Date(new Date().setDate(new Date().getDate() - 1)),
      minMonthStatements: MIN_MONTH_STATEMENTS,
      maxMonthStatements: this.getMaxMonth(),
      minDateStatements: MIN_DATE_STATEMENTS,
      isCurrentMerchantAllowed: true,
      statementForm: {
        isValid: true,
        chosenMonth: this.getMaxMonth(),
        chosenDate: '',
      },
      isMonthly: true,
      errors: {
        hasNetworkError: false,
        hasNoDatesError: false,
        invalidDates: false,
        datesEarlierThanLimit: false,
      },
      latestRequest: {
        status: '',
        requestedOn: null,
        from: null,
        to: null,
        email: null,
        id: null,
        lastUpdated: null,
      },
      isRequestSuccessful: false,
      isRequestSpam: false,
      isRequestError: false,

      gstForm: {
        isValid: true,
        chosenFromMonth: '',
        chosenToMonth: '',
      },
      minMonthGst: MIN_MONTH_GST,
      maxMonthGst: this.getMaxMonth(),
      gstRecipients: [],
      newGstRecipient: '',
      isGstEmailSuccessful: '',
      gstEmailErrorMessage: false,
      gstPdfErrorMsg: '',
    };
  },
  methods: {
    isStatementFormValid() {
      let isValid = true;
      if (this.isMonthly && !this.isDateValid(this.statementForm.chosenMonth)) {
        isValid = false;
      } else if (!this.isMonthly && !this.isDateValid(this.statementForm.chosenDate)) {
        isValid = false;
      } else if (!this.isMonthly && !this.isDailyStatementsAllowed) {
        isValid = false;
      }
      return isValid;
    },

    /**
     * Builds the payload to be sent to the store, and ultimately to Back-end.
     */
    buildPayload() {
      const params = {};
      if (this.statementForm.isValid) {
        params.merchant_id = this.$store.getters.activeMerchantGetter.id;
      }
      if (this.isMonthly) {
        const toDate = this.getNextMonth(this.statementForm.chosenMonth);
        params.to_date = this.getLocalMonth(toDate) + '-01';
        params.from_date = this.statementForm.chosenMonth + '-01';
      } else {
        const wantedDay = new Date(this.statementForm.chosenDate);
        let toDate = new Date(wantedDay);
        toDate.setDate(wantedDay.getDate() + 1);
        params.from_date = this.getLocalDate(wantedDay);
        params.to_date = this.getLocalDate(toDate);
      }

      return params;
    },

    /**
     * Emits the statement request to the backend, with the parameters chosen by user
     * @returns request status
     */
    requestStatements() {
      if (!this.isStatementFormValid()) {
        return;
      }
      this.resetErrors();
      this.resetBannersStatements();
      const payload = this.buildPayload();
      if (payload) {
        this.$store
          .dispatch('requestStatements', payload)
          .then((res) => {
            this.isRequestSpam = false;
            this.isRequestSuccessful = false;
            this.isRequestError = false;
            if (res.status === 200) {
              this.getLatestRequest();
              this.isRequestSuccessful = true;
            }
          })
          .catch(() => {
            this.isRequestError = true;
          });
      }
    },

    /**
     * Emits the GST invoice request
     */
    requestGstInvoicesPDF() {
      if (!this.isGstDatesValid()) {
        return;
      }
      const monthsList = this.getListOfMonths(
        this.gstForm.chosenFromMonth,
        this.gstForm.chosenToMonth
      );
      this.gstPdfErrorMsg = '';
      this.$store
        .dispatch('generateGstInvoicePDF', monthsList)
        .then((response) => {
          const blobData =
            monthsList.length > 1
              ? new Blob([response.data], { type: 'application/zip' })
              : new Blob([response.data], { type: 'application/pdf' });
          const fileUrl = window.URL.createObjectURL(blobData);

          const downloadLink = document.createElement('a');
          downloadLink.href = fileUrl;
          downloadLink.download =
            monthsList.length > 1
              ? `GSTInvoice_${monthsList[0]}_${monthsList[1]}`
              : `GSTInvoice_${monthsList[0]}`;
          downloadLink.click();
          window.URL.revokeObjectURL(fileUrl);
        })
        .catch((e) => {
          if (e.response.status === 404) {
            this.gstPdfErrorMsg = 'No invoice found for this merchant.';
          } else {
            this.gstPdfErrorMsg = `Error while generating the GST invoice PDF. Please retry.`;
          }
        });
    },

    /**
     * Request BE to send the GSt invoice by email to the selected Recipients
     */
    sendGstInvoiceByEmail() {
      if (!this.isGstDatesValid()) {
        return;
      }
      const monthsList = this.getListOfMonths(
        this.gstForm.chosenFromMonth,
        this.gstForm.chosenToMonth
      );
      const payload = {
        months: monthsList,
        emailList: this.gstRecipients,
      };
      // reset result values
      this.isGstEmailSuccessful = false;
      this.gstEmailErrorMessage = false;
      this.$store
        .dispatch('sendGstInvoiceEmail', payload)
        .then((res) => {
          console.log(res);
          this.isGstEmailSuccessful = true;
        })
        .catch((e) => {
          this.isGstEmailSuccessful = false;
          this.gstEmailErrorMessage = `Error while sending GST emails (${e.message}). Please retry.`;
        });
    },

    /**
     * Pings the Backend to retrieve the latest request made by the selected account
     */
    getLatestRequest() {
      this.$store
        .dispatch('getLatestRequestStatus', {
          api_id: this.$store.getters.activeAccountGetter.apiId,
        })
        .then((res) => {
          try {
            this.latestRequest = this.formatRequest(res.data.data);
          } catch (e) {
            console.error(e);
          }
        })
        .catch((err) => {
          throw err;
        });
    },

    /**
     * Cheks wether dates are both defined, not spaced by more than 31days, and that fromDate is earlier than toDate
     * @param {Date} fromD
     * @param {Date} toD
     * @returns {Boolean}
     */
    isDateValid(input) {
      this.resetErrors();
      let res = true;
      if (!input) {
        this.errors.hasNoDatesError = true;
        return false;
      }
      // before min and after max are handled by HTML5 date input
      return res;
    },

    isGstDatesValid() {
      // empty dates
      if (!this.gstForm.chosenFromMonth) {
        this.gstPdfErrorMsg =
          'Please select a date before requesting document.';
        return false;
      }
      // Earlier than defined earliest month possible
      if (
        (this.gstForm.chosenToMonth &&
          this.gstForm.chosenToMonth < MIN_MONTH_GST) ||
        this.gstForm.chosenFromMonth < MIN_MONTH_GST
      ) {
        this.gstPdfErrorMsg = `Invalid Dates. Earliest possible month is ${MIN_MONTH_GST}.`;
        return false;
      }
      // From earlier than To
      if (
        this.gstForm.chosenToMonth &&
        this.gstForm.chosenFromMonth > this.gstForm.chosenToMonth
      ) {
        this.gstPdfErrorMsg =
          'To date earlier than From date. Please select your dates in chronological order.';
        return false;
      }
      // No more than 12 months
      if (
        this.getListOfMonths(
          this.gstForm.chosenFromMonth,
          this.gstForm.chosenToMonth
        ).length > 12
      ) {
        this.gstPdfErrorMsg =
          'Too many months selected. Please select up to 12 months.';
        return false;
      }
      // Later than today
      if (
        this.getMaxMonth() < this.gstForm.chosenToMonth ||
        this.getMaxMonth() < this.gstForm.chosenFromMonth
      ) {
        this.gstPdfErrorMsg = 'Invalid dates. Please select earlier dates.';
        return false;
      }
      this.gstPdfErrorMsg = '';
      return true;
    },

    /**
     * Resets the errors
     */
    resetErrors() {
      this.errors.invalidDates = false;
      this.errors.hasNoDatesError = false;
      this.errors.hasNetworkError = false;
      this.errors.datesEarlierThanLimit = false;
    },

    resetBannersStatements() {
      this.isRequestSuccessful = false;
      this.isRequestSpam = false;
      this.isRequestError = false;
    },

    resetForm() {
      this.statementForm = {
        isValid: true,
        chosenMonth: '',
      };
    },

    handleReset() {
      this.resetForm();
      this.resetErrors();
    },

    toggleSuccessMessage() {
      this.isRequestSuccessful = !this.isRequestSuccessful;
    },

    addGstRecipient() {
      if (!this.isEmail(this.newGstRecipient)) {
        return;
      }
      if (this.gstRecipients.includes(this.newGstRecipient)) {
        this.newGstRecipient = '';
        return;
      }
      this.gstRecipients.push(this.newGstRecipient);
      this.newGstRecipient = '';
    },

    removeGstRecipient(index) {
      this.gstRecipients.splice(index, 1);
    },

    /**
     * returns date formatted as "YYYY-MM-DD, HH:mm:ss". If empty returns '-'
     * @param {*} date
     */
    formatDate(date) {
      if (!date) return '-';
      else return date.split('T')[0];
    },

    /**
     * returns date with time formatted as "ll, HH:mm". If empty returns '-'
     * @param {*} date
     */
    formatDateWithTime(date) {
      if (!date) return '-';
      else
        return `${this.formatToLocalDate(date)}, ${this.formatToLocalTime(
          date
        )}`;
    },

    /**
     *
     * @param {
     *  data {
     *   data {
     *    id,status, params: {from, to}, email, created_at, updated_at
     *   }
     *  }
     * } res
     */
    formatRequest(res) {
      if (!res) {
        return;
      }
      return {
        id: res.id,
        status: res.status,
        from: res.params.from,
        to: res.params.to,
        email: res.email,
        requestedOn: res.created_at,
        lastUpdated: res.updated_at,
      };
    },

    getMaxMonth() {
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() - 1); // Date object take care of the January -1 cases
      return `${this.getLocalMonth(maxDate)}`;
    },

    /**
     * GST version of the getMaxMonth. Adds a 12 months range limit condition
     */
    getMaxMonthGST() {
      const lastCompleteMonth = this.getMaxMonth();
      if (!this.gstForm.chosenFromMonth) {
        return lastCompleteMonth;
      }
      const chosenFromDate = new Date(this.gstForm.chosenFromMonth);
      chosenFromDate.setMonth(chosenFromDate.getMonth() + 12); // max 12 months allowed
      const formattedMaxRange = `${this.getLocalMonth(chosenFromDate)}`;
      return lastCompleteMonth < formattedMaxRange
        ? lastCompleteMonth
        : formattedMaxRange; // return earliest out of the 2
    },

    getLocalMonth(date) {
      return date.toJSON().substring(0, 7);
    },

    getLocalDate(date) {
      return date.toJSON().split('T')[0];
    },

    /**
     * Returns the next month. Fixes the setMonth() bug for countries with daylight savings changes.
     * TBT-154
     * @param {*} date
     */
    getNextMonth(date) {
      const originalDate = new Date(date);
      const toDate = new Date(date);
      toDate.setMonth(originalDate.getMonth() + 1);
      // testing month difference
      if (
        toDate.toJSON().substring(0, 7) ===
        originalDate.toJSON().substring(0, 7)
      ) {
        toDate.setMonth(toDate.getMonth() + 1); // add another month in case daylight savings changes return 31st 23:00 on getMonth
      }
      return toDate;
    },

    /**
     * Gets a list of all the months between the 2 given dates
     * @param {*} startDate
     * @param {*} endDate
     */
    getListOfMonths(startDate, endDate) {
      if (!endDate) {
        return [startDate];
      }
      if (!startDate) {
        return [endDate];
      }
      const start = startDate.split('-');
      const end = endDate.split('-');
      const startYear = parseInt(start[0]);
      const endYear = parseInt(end[0]);
      const dates = [];

      for (let i = startYear; i <= endYear; i++) {
        const endMonth = i != endYear ? 11 : parseInt(end[1]) - 1;
        const startMonth = i === startYear ? parseInt(start[1]) - 1 : 0;
        for (
          let j = startMonth;
          j <= endMonth;
          j = j > 12 ? j % 12 || 11 : j + 1
        ) {
          const month = j + 1;
          const displayMonth = month < 10 ? '0' + month : month;
          dates.push(`${i}-${displayMonth}`);
        }
      }
      return dates;
    },

    /**
     * Month in JavaScript is 0-indexed (January is 0, February is 1, etc), 
     * but by using 0 as the day it will give us the last day of the prior
     * month. So passing in 1 as the month number will return the last day
     * of January, not February
     */
    maxDaysInMonth(month, year) {
      return new Date(year, month, 0).getDate();
    }
  },
  computed: {
    activeMerchant() {
      return this.$store.getters.storedActiveMerchantGetter
        ? this.$store.getters.storedActiveMerchantGetter
        : this.$store.getters.activeMerchantGetter;
    },
    gstChosenFromMonth() {
      return this.gstForm.chosenFromMonth;
    },
    isDailyStatementsAllowed() {
      const allowedMerchants = [
        // Test accounts
        'mkey-cli1gcsqr000028ho6r5her4p', // Triple-A Tester (USD)
        'mkey-cl5kicv8g0000a46m6oljcste', // Jera test 
        'mkey-ckc4merl9048dwqns4kjz5cgn', // PROD Antonio
        'mkey-cllabj7ia0000nskoghgq3ive', // Guek Ling test

        // Merchants - For Settlements
        'mkey-ckytiyev18m7c95thgwo1byop', // AMEEX Mobile Exchange SA   
        'mkey-cl59fc60p0es3wbisfhpo16fu', // BIGPAY SINGAPORE PTE LTD
        'mkey-cl5jab7os70ul7kis3cz4b76o', // Bigo Technology Pte Ltd
        'mkey-cksqwdg1x0000f7thf3k152lh', // Crazy Wander Tech Limited
        'mkey-cl1f1tiv10hx0hyth08wsapkn', // Demica Mediamar Sl
        'mkey-cksqw8qnj00009vth0fcv5da5', // Dragonpay
        'mkey-cl2sptfn00000flth5uke7qka', // Gamer2Gamer Global Pte Ltd (SGD)
        'mkey-cl2spvpfm0000wlthazmt91x9', // Gamer2Gamer Global Pte Ltd (USD)
        'mkey-cl3as6yne0000wgth78vk2i3y', // Gamer2Gamer Global Pte Ltd (EUR)
        'mkey-cl3asbszf0000qeth0lkldcpj', // Gamer2Gamer Sdn Bhd (MYR)
        'mkey-ckznpbply2b4496thfl7uf3my', // NOVELSHIP PTE LTD
        'mkey-cl3v3gf6d0000oqth48ww5k1l', // NOVELSHIP PTE LTD (Payouts)
        'mkey-cl44y5mze00009tthh8n5dnyw', // NOVELSHIP PTE LTD (USD Payouts)
        'mkey-cl2em9mvy0000vjthd97sc8cv', // OffGamers Sdn Bhd (MYR)
        'mkey-cl2sqe2hs00008uthe8dz30s7', // OffGamers Global Pte Ltd
        'mkey-cl2sqfuyj0000iwthaujo4m0z', // OffGamers Global Pte Ltd (USD)
        'mkey-cl3arz4p40000a4thdt1r0mbo', // OffGamers Global Pte Ltd (EUR)
        'mkey-cl14o0hia0000zjth40anfa82', // PayCorp Group (PTY) Ltd
        'mkey-cknyda21q0000ydnsa98tcznp', // Razer Merchant Services (SG) Pte Ltd
        'mkey-cl3chnpgu0000h1thfqcs5185', // Razer Store (USD)
        'mkey-cl3chuvbx0000v6thdy2q06wb', // Razer Store (SGD)
        'mkey-cl3ci1gtq0000lath2d6727xz', // RMS (MYR)
        'mkey-cl3inde4y0000fmthaci59tww', // Razer Cafe
        'mkey-cl51t42yc00001idj0uvhhges', // RMS (SGD)
        'mkey-cl51t8fao00005pdjeaokakpx', // RMS (USD)
        'mkey-cl3d6wb5m1xyzv0th5t8qcnap', // SC Sky Drone Technologies SRL
        'mkey-cl9es9yf82vahqvisbzr244an', // Sc Drp Drones Srl
        'mkey-ckwacchp70000tyth86td7ki7', // Sea Game Mall Sdn Bhd
        'mkey-ckly6xjfo1bzj6knscc7kgknu', // Swiftreload Inc
        'mkey-cko13nnc70000wxns35h24lv3', // Towerking
        'mkey-cksyruchv0000xsthhyy4a6oc', // UAB Zen.com (Terminal 6)
        'mkey-ckw1t47rn0000zjthel8ubts5', // UAB Zen.com (Terminal 7)
        'mkey-cl2rh0rgh0000bdthh6h58lz4', // UAB Zen.com (Terminal 10)
        'mkey-cl28qjsqt0000goth9itw7hq7', // Uab Zen.Com - G2A
        'mkey-cl0t8pyqq00cgymthco473rjg', // Zabava GmbH
        'mkey-clg4yuowe004ldxmz1qo9gvp1', // Core Cloud Technology Pte Ltd
        'mkey-cl7tvunm03pz2yiisbxc5fb6o', // lintu Cherian TBT-402
        'mkey-clslzrf490000k9djcgich938', // Crypto Wallet DU TBT-413

        // Merchants - Requested by Ops
        'mkey-cldyi3q3l002gqbmza06i8k81', // Gname.com Pte Ltd Payout
        'mkey-clkw145do002lafmz86dg0nqo', // Gnamehk Limited
        'mkey-cldyi2hvp0025qbmzco6wa2sb', // Gname.com Pte Ltd Payment
        'mkey-cl2sq8hp2000024th3b0jflm2', // G2GID (IDR)
      ];
      return allowedMerchants.includes(this.activeMerchant.merchant_key);
    },
  },
  components: {
    Tag,
  },
  mounted() {
    this.getLatestRequest();
    this.statementForm.chosenDate = this.getLocalDate(this.YESTERDAY);
    if (this.isDailyStatementsAllowed) {
      this.isMonthly = false;
    } else {
      this.isMonthly = true;
    }
  },
  watch: {
    gstChosenFromMonth() {
      this.maxMonthGst = this.getMaxMonthGST();
    },
  },
};
</script>
<style lang="scss">
.statements-wrapper {
  h1 {
    text-transform: uppercase;
    font-size: 32px;
    font-weight: bold;
    color: $color-text;
    margin-bottom: $margin-m;
  }

  p {
    max-width: 800px;
    color: $color-text;

    span {

      &.confirm,
      &.done,
      &.good {
        background-color: green;
        color: white;
      }

      &.short {
        background-color: white;
        border-color: orange;
        color: orange;
      }
    }
  }

  form {
    display: flex;
    align-items: flex-end;
    margin-top: $margin-m;
    flex-wrap: wrap;
    margin-bottom: $margin-m;

    fieldset {
      border: none;
      display: flex;
      flex-wrap: wrap;
      margin-bottom: $margin-s;
      margin-right: $margin-m;
      align-items: flex-end;
    }

    label {
      font-size: 12px;
      margin-right: $margin-s;
      display: block;

      input {
        display: block;
        width: 100%;
        height: 38px;
        border-radius: 5px;
        padding: $margin-xs;
        border: 0.5px solid lightgrey;

        &.invalid {
          border-color: red;
        }
      }


      &:last-of-type {
        margin-right: 0;
      }

      input,
      select {
        display: block;
        width: 100%;
        height: 38px;
        border-radius: 5px;
        padding: 3px;
        border: none;

        &:hover {
          cursor: pointer;
        }
      }

      &:hover {
        cursor: pointer;
      }

      &.checkbox {
        display: flex;
        margin-bottom: 6px;

        input {
          width: 18px;
          height: 18px;
          margin-right: 6px;
          margin-left: 12px;
        }

      }
    }

    .action-group {
      display: flex;
      align-items: flex-end;
      margin-bottom: $margin-s;
      width: 100%;
      margin-top: 12px;
    }
  }

  ol.errors {
    display: block;
    width: 100%;
    margin-top: $margin-s;
    color: red;

    li {
      margin-bottom: $margin-s;
    }
  }

  .result {
    padding: $margin-s;
    border-radius: 6px;
    border: 1px solid;
    margin-top: $margin-s;
    max-width: 600px;
    transition: opacity 0.3s ease-in-out;
    transition: visibility 0.1s ease-in-out;
    opacity: 1;

    &.success {
      background-color: aliceblue;
      border-color: lightblue;
    }

    &.deny {
      background-color: rgb(254, 219, 225);
      border-color: rgb(254, 157, 157);
    }

    &.spam {
      background-color: antiquewhite;
      border-color: orange;
    }

    &.hide {
      opacity: 0;
      height: 0;
    }
  }

  article.latest-request {
    margin: $margin-m $margin-s;
    display: flex;
    width: fit-content;
    flex-direction: column;

    dl {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding: $margin-s;
      background-color: rgb(249, 246, 239);
      border: 1px solid $color-text;
      border-radius: 6px;
      margin-top: $margin-s;

      div {
        display: flex;
        flex-direction: column;
        margin-right: $margin-m;

        dt {
          font-weight: bold;
        }

        dd {
          margin-top: $margin-xs;
        }
      }
    }
  }

  // Transitions
  .slide-fade-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-fade-leave-active {
    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
  }

  article.gst-wrapper {
    margin-top: $margin-xl;

    h2 {
      margin-bottom: $margin-m;
    }

    p.error {
      margin-top: $margin-m;
      color: red;
    }

    p.success {
      margin-top: $margin-m;
      color: green;
    }

    form {
      display: block;

      fieldset {
        display: flex;
        flex-direction: row;
        margin-bottom: 0;

        legend {
          font-weight: bold;
        }

        input[type='email'] {
          min-width: 200px;
        }

        button.save {
          margin-left: $margin-m;
        }
      }
    }

    .gst-recipients {
      width: 100%;
      margin-top: $margin-l;

      h3 {
        width: 100%;
      }

      ul {
        width: 100%;
        margin-top: $margin-m;
        margin-bottom: $margin-m;

        li {
          margin-bottom: $margin-xs;
        }
      }

      label {
        margin-right: $margin-s;
      }

      button.btn.icon-btn.plus {
        &:before {
          background-image: url('../../assets/icons/add_blue.svg');
        }

        &:hover {
          &::before {
            background-image: url('../../assets/icons/add_white.svg');
          }
        }
      }

      button.remove {
        background-color: white;
        color: red;
        border: 1px solid red;
        padding: 5px 8px;
        margin-left: $margin-m;

        &:hover {
          color: white;
          background-color: red;
        }
      }
    }
  }
}
</style>
